/**
 * LINE ニュース画像セクション エディタ（Phase 24b 改修：ImageGalleryDialog 連携）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5
 *
 * - storage/line/imgs/ から既存画像を選択 or 新規アップロード
 * - 新規アップロード時は client canvas で 1024×1024 / 10MB 内にリサイズ
 * - ファイル命名: line/imgs/{timestamp}_{rand}.jpg（フラット構造）
 */
import { ImageIcon, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ImageGalleryDialog } from '@/components/common/ImageGalleryDialog';
import { useEffect, useState } from 'react';

import { resizeImage } from '@/lib/imageResize';

import type { LineNewsSection } from '@goenhub/shared';

const LINE_MAX_DIMENSION = 1024;
const LINE_MAX_BYTES = 10 * 1024 * 1024;
const STORAGE_PATH = 'line/imgs';

interface Props {
  /** newsId は記録（互換のため残置）*/
  newsId: string;
  section: LineNewsSection;
  onChange: (next: LineNewsSection) => void;
  onDelete: () => void;
}

export function ImageSectionEditor({ section, onChange, onDelete }: Props) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [dimensions, setDimensions] = useState<{ w: number; h: number; bytes: number } | null>(
    section.imageWidth && section.imageHeight
      ? { w: section.imageWidth, h: section.imageHeight, bytes: section.imageBytes ?? 0 }
      : null,
  );

  // 画像 URL 変更時にメタデータ取得
  useEffect(() => {
    if (!section.imageUrl) {
      setDimensions(null);
      return;
    }
    if (section.imageWidth && section.imageHeight) {
      setDimensions({
        w: section.imageWidth,
        h: section.imageHeight,
        bytes: section.imageBytes ?? 0,
      });
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      setDimensions({ w: img.naturalWidth, h: img.naturalHeight, bytes: 0 });
      onChange({
        ...section,
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
      });
    };
    img.src = section.imageUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.imageUrl]);

  const removeImage = () => {
    onChange({
      ...section,
      imageUrl: undefined,
      imageWidth: undefined,
      imageHeight: undefined,
      imageBytes: undefined,
    });
    setDimensions(null);
  };

  return (
    <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)]">
      <div className="flex items-center justify-between p-2 bg-[var(--bg-hover)] border-b border-[var(--border)] rounded-t text-xs">
        <div className="flex items-center gap-2">
          <ImageIcon className="size-3.5 text-[var(--accent-purple)]" />
          <span className="font-semibold">画像</span>
          <code className="text-[10px] font-mono text-[var(--text-muted)]">image</code>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onDelete} title="削除">
          <Trash2 className="size-3" />
        </Button>
      </div>
      <div className="p-3">
        {section.imageUrl ? (
          <div className="space-y-2">
            <div className="relative inline-block">
              <img
                src={section.imageUrl}
                alt=""
                className="max-w-full max-h-72 rounded border border-[var(--border)]"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2 size-6 rounded-full bg-[var(--accent-red)] text-white flex items-center justify-center shadow hover:scale-110 transition-transform"
                title="画像を削除"
              >
                <X className="size-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setGalleryOpen(true)}
              >
                <ImageIcon className="size-3.5 mr-1" />
                画像を変更
              </Button>
              {dimensions && (
                <span className="text-[10px] text-[var(--text-muted)] ml-auto">
                  {dimensions.w} × {dimensions.h}
                  {dimensions.bytes > 0 &&
                    ` ・ ${(dimensions.bytes / 1024).toFixed(0)} KB`}
                </span>
              )}
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setGalleryOpen(true)}
          >
            <ImageIcon className="size-3.5 mr-1" />
            画像を選択
          </Button>
        )}
      </div>

      <ImageGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        title="LINE ニュース画像を選択"
        storagePath={STORAGE_PATH}
        maxBytes={LINE_MAX_BYTES}
        hintText="既存の LINE 画像から選択するか、新しい画像をアップロードしてください（1024×1024 / 10MB 内に自動リサイズ）。"
        beforeUpload={async (file) => {
          try {
            const resized = await resizeImage(file, {
              maxDimension: LINE_MAX_DIMENSION,
              maxBytes: LINE_MAX_BYTES,
            });
            return { blob: resized.blob, ext: resized.ext };
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            // ImageGalleryDialog 側でアップロード処理は中断される
            throw new Error(`画像処理失敗: ${msg}`);
          }
        }}
        onSelect={(url) => {
          onChange({
            ...section,
            imageUrl: url,
            // 画像メタは useEffect で再取得
            imageWidth: undefined,
            imageHeight: undefined,
            imageBytes: undefined,
          });
        }}
      />
    </div>
  );
}
